import { EVENT_TYPES, EVENT_STATUS, type EventType, type EventStatus } from '@wineryxplorer/shared';
import { FormSection } from '../ui/FormSection';
import type { EventFormErrors } from './types';

export interface EventBasicInfoFieldsProps {
  name: string;
  onNameChange: (value: string) => void;
  eventType: EventType;
  onEventTypeChange: (value: EventType) => void;
  status: EventStatus;
  onStatusChange: (value: EventStatus) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  errors: EventFormErrors;
  touched: Record<string, boolean>;
  onBlur: (field: string) => void;
  isSubmitting: boolean;
}

export const EventBasicInfoFields = ({
  name,
  onNameChange,
  eventType,
  onEventTypeChange,
  status,
  onStatusChange,
  description,
  onDescriptionChange,
  errors,
  touched,
  onBlur,
  isSubmitting,
}: EventBasicInfoFieldsProps) => (
  <FormSection title="Basic Information">
    {/* Event Name */}
    <div>
      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
        Event Name <span className="text-red-500">*</span>
      </label>
      <input
        id="name"
        type="text"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        onBlur={() => onBlur('name')}
        disabled={isSubmitting}
        placeholder="e.g., Summer Wine Tasting"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.name && errors.name ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        maxLength={200}
      />
      {touched.name && errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
    </div>

    {/* Event Type */}
    <div>
      <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-1">
        Event Type <span className="text-red-500">*</span>
      </label>
      <select
        id="eventType"
        value={eventType}
        onChange={(e) => onEventTypeChange(e.target.value as EventType)}
        onBlur={() => onBlur('eventType')}
        disabled={isSubmitting}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.eventType && errors.eventType ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      >
        {EVENT_TYPES.map((type) => (
          <option key={type} value={type}>
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </option>
        ))}
      </select>
      {touched.eventType && errors.eventType && (
        <p className="mt-1 text-sm text-red-600">{errors.eventType}</p>
      )}
    </div>

    {/* Status */}
    <div>
      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
        Status <span className="text-red-500">*</span>
      </label>
      <select
        id="status"
        value={status}
        onChange={(e) => onStatusChange(e.target.value as EventStatus)}
        disabled={isSubmitting}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      >
        {EVENT_STATUS.map((statusOption) => (
          <option key={statusOption} value={statusOption}>
            {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
          </option>
        ))}
      </select>
      <p className="mt-1 text-sm text-gray-500">
        Draft events are only visible to organizers and venue admins. Published events are visible
        to everyone.
      </p>
    </div>

    {/* Description */}
    <div>
      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
        Description
      </label>
      <textarea
        id="description"
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
        disabled={isSubmitting}
        placeholder="Describe your event..."
        rows={4}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      />
    </div>
  </FormSection>
);
